(function (global) {
  "use strict";
  const mounted = new WeakMap();
  const instances = new Set();
  const motion = global.matchMedia("(prefers-reduced-motion: reduce)");
  const finePointer = global.matchMedia("(hover: hover) and (pointer: fine)");
  const formatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  function formatAmount(value) {
    if (value === null || value === undefined || typeof value === "boolean" || (typeof value === "string" && !value.trim())) return null;
    const amount = Number(value);
    if (!Number.isFinite(amount) || amount < 0 || amount > Number.MAX_SAFE_INTEGER / 100) return null;
    const rounded = Math.round((amount + Number.EPSILON) * 100) / 100;
    const hasCents = Math.abs(rounded - Math.round(rounded)) > .001;
    return "$" + (hasCents ? rounded.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : formatter.format(rounded));
  }
  function poseForPoint(x, y) {
    x = clamp(x, 0, 1); y = clamp(y, 0, 1);
    const rx = (.5 - y) * 16;
    const ry = (x - .5) * 22;
    const a = rx * Math.PI / 180, b = ry * Math.PI / 180;
    const normal = [Math.sin(b) * Math.cos(a), -Math.sin(a), Math.cos(b) * Math.cos(a)];
    // Halfway light direction: the foil flashes only near a reflecting angle.
    const h = [.075, -.091, .993];
    const len = Math.hypot(...h);
    const dot = Math.max(0, normal.reduce((sum, value, i) => sum + value * h[i] / len, 0));
    const specular = Math.pow(dot, 180);
    return { rx, ry, rz: (x - .5) * 2.4, scale: 1.12, fx: 15 + x * 70, fy: 15 + y * 70,
      angle: 115 + (x - .5) * 48 + (y - .5) * 18,
      sweep: 16 + (x * .78 + y * .22) * 68,
      flash: clamp((specular - .18) / .82, 0, 1) };
  }
  const resting = () => ({ rx: 0, ry: 0, rz: 0, scale: 1, fx: 50, fy: 50, angle: 115, sweep: 50, flash: 0 });
  function mountOne(ticket) {
    if (mounted.has(ticket)) return mounted.get(ticket);
    const surface = ticket.closest("[data-ticket-surface]") || ticket.closest(".ks-ccard") || ticket.parentElement;
    const valueBox = ticket.querySelector(".ks-ticket__value");
    const ink = ticket.querySelector(".ks-ticket__ink");
    if (!surface || !valueBox || !ink) return null;
    let pose = resting(), target = resting(), frame = 0, lastTime = 0, hovered = false, pointer = null;
    let observer = null, destroyed = false;
    const measure = document.createElement("canvas").getContext("2d");
    function fit() {
      if (destroyed || !measure) return;
      const width = valueBox.clientWidth, height = valueBox.clientHeight;
      if (!width || !height) return;
      const style = global.getComputedStyle(ink);
      measure.font = `${style.fontWeight} 100px ${style.fontFamily}`;
      const units = measure.measureText(ink.textContent).width / 100;
      // Keep multi-digit figures tall before reducing size, as on narrow printed tickets.
      const size = Math.min(height * .98, width * .96 / Math.max(.1, units * .62));
      const horizontalScale = Math.min(1, width * .96 / Math.max(.1, units * size));
      ink.style.fontSize = size.toFixed(3) + "px";
      ink.style.transform = `scaleX(${horizontalScale.toFixed(5)})`;
    }
    function setAmount(value) {
      const label = formatAmount(value);
      if (label === null) return false;
      ticket.dataset.saving = String(Math.round(Number(value) * 100) / 100);
      ticket.setAttribute("aria-label", "Save " + label);
      ink.textContent = label; ink.dataset.amount = label;
      fit(); return true;
    }
    function paint() {
      const s = ticket.style;
      s.setProperty("--ticket-rx", pose.rx.toFixed(3) + "deg");
      s.setProperty("--ticket-ry", pose.ry.toFixed(3) + "deg");
      s.setProperty("--ticket-rz", pose.rz.toFixed(3) + "deg");
      s.setProperty("--ticket-scale", pose.scale.toFixed(5));
      const lift = clamp((pose.scale - 1) / .12, 0, 1);
      s.setProperty("--ticket-shadow-y", (3 + lift * 4).toFixed(3) + "px");
      s.setProperty("--ticket-shadow-blur", (4 + lift * 5).toFixed(3) + "px");
      s.setProperty("--ticket-shadow-alpha", (.14 + lift * .06).toFixed(4));
      s.setProperty("--foil-x", pose.fx.toFixed(2) + "%");
      s.setProperty("--foil-y", pose.fy.toFixed(2) + "%");
      s.setProperty("--foil-angle", pose.angle.toFixed(2) + "deg");
      s.setProperty("--foil-sweep", pose.sweep.toFixed(2) + "%");
      s.setProperty("--foil-flash", pose.flash.toFixed(4));
    }
    function schedule() { if (!frame && !destroyed) frame = global.requestAnimationFrame(tick); }
    function tick(time) {
      frame = 0;
      if (destroyed) return;
      if (pointer && hovered) {
        const r = surface.getBoundingClientRect();
        if (r.width && r.height) target = poseForPoint((pointer.x - r.left) / r.width, (pointer.y - r.top) / r.height);
        pointer = null;
      }
      const dt = lastTime ? Math.min(48, Math.max(1, time - lastTime)) : 16;
      lastTime = time;
      const lerp = 1 - Math.exp(-dt / 65);
      const scaleLerp = 1 - Math.exp(-dt / (hovered ? 90 : 110));
      let distance = 0;
      for (const key of Object.keys(pose)) {
        pose[key] += (target[key] - pose[key]) * (key === "scale" ? scaleLerp : lerp);
        distance = Math.max(distance, Math.abs(target[key] - pose[key]) * (key === "scale" ? 100 : 1));
      }
      paint();
      if (distance > .005) schedule();
      else { pose = { ...target }; paint(); lastTime = 0; if (!hovered) delete ticket.dataset.moving; }
    }
    function move(event) {
      if (motion.matches || !finePointer.matches || event.pointerType === "touch") return;
      hovered = true; ticket.dataset.moving = "true";
      pointer = { x: event.clientX, y: event.clientY }; schedule();
    }
    function leave() { hovered = false; pointer = null; target = resting(); schedule(); }
    function stop() {
      hovered = false; pointer = null;
      if (frame) global.cancelAnimationFrame(frame);
      frame = 0; lastTime = 0; pose = resting(); target = resting();
      delete ticket.dataset.moving; paint();
    }
    function handlePreference() { if (motion.matches || !finePointer.matches) stop(); }
    function visibility() { if (document.hidden) stop(); }
    surface.addEventListener("pointerenter", move, { passive: true });
    surface.addEventListener("pointermove", move, { passive: true });
    surface.addEventListener("pointerleave", leave);
    surface.addEventListener("pointercancel", stop);
    global.addEventListener("blur", stop);
    document.addEventListener("visibilitychange", visibility);
    motion.addEventListener("change", handlePreference); finePointer.addEventListener("change", handlePreference);
    if (typeof global.ResizeObserver === "function") { observer = new global.ResizeObserver(fit); observer.observe(valueBox); }
    const image = ticket.querySelector(".ks-ticket__paper");
    image?.addEventListener("load", fit);
    document.fonts?.ready.then(fit);
    const api = { ticket, setAmount, fit, reset: stop, destroy() {
      stop(); destroyed = true; observer?.disconnect(); image?.removeEventListener("load", fit);
      surface.removeEventListener("pointerenter", move); surface.removeEventListener("pointermove", move);
      surface.removeEventListener("pointerleave", leave); surface.removeEventListener("pointercancel", stop);
      global.removeEventListener("blur", stop); document.removeEventListener("visibilitychange", visibility);
      motion.removeEventListener("change", handlePreference); finePointer.removeEventListener("change", handlePreference);
      mounted.delete(ticket); instances.delete(api);
    } };
    mounted.set(ticket, api); instances.add(api);
    // Prefer Liquid-rendered money format (locale currency + separators).
    const serverLabel = (ink.dataset.amount || ink.textContent || "").trim();
    if (serverLabel && ticket.dataset.saving != null && ticket.dataset.saving !== "") {
      if (!ticket.getAttribute("aria-label")) ticket.setAttribute("aria-label", "Save " + serverLabel);
      fit();
    } else {
      setAmount(ticket.dataset.saving || 450);
    }
    return api;
  }
  function mount(root = document) {
    const tickets = [...root.querySelectorAll(".ks-ticket")];
    if (root.matches?.(".ks-ticket")) tickets.unshift(root);
    return tickets.map(mountOne).filter(Boolean);
  }
  global.KSTicketFoil = { mount, formatAmount, poseForPoint, setAmount(ticket, value) {
    return mountOne(ticket)?.setAmount(value) ?? false;
  }, destroyWithin(root) { [...instances].forEach(api => { if (root.contains(api.ticket)) api.destroy(); }); } };
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => mount(), { once: true });
  else mount();
  document.addEventListener("shopify:section:load", event => mount(event.target));
  document.addEventListener("shopify:section:unload", event => global.KSTicketFoil.destroyWithin(event.target));
})(window);
